import {
  FacetKey,
  Facets,
  SearchParams,
  SelectedFacet,
  IndexType,
  PAGINATION_SIZE,
} from '@lessonup/typescript-core/domain';
import { useCallbackIfParamsChanged, useDeepCompareMemoize, useContextGenerator } from '@lessonup/typescript-core/ui';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import React, { useCallback, useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { isUserSignedIn } from '../../../redux/selectors';
import { AppSearchService } from '../../../services/AppServices';

export interface SearchContextProps {
  currentParams: () => SearchParams;
  selectedFacets: SelectedFacet[];
  selectedText: string | undefined;
  setSearchText: (value: string | undefined) => void;
  setFacet: (key: FacetKey, value: string[]) => void;
  resetFacet: (key: FacetKey) => void;
  paginate: () => void;
}

type Context = SearchContextProps | undefined;

export const SearchContext = React.createContext<Context>(undefined);

// eslint-disable-next-line react-hooks/rules-of-hooks -- auto-generated TECH-258
export const useSearchContext = useContextGenerator(SearchContext, 'SearchContext');

export type FacetAction =
  | {
      type: 'setValueFacet';
      key: FacetKey;
      value: string[];
    }
  | {
      type: 'resetFacet';
      key: FacetKey;
    }
  | {
      type: 'setAllFacets';
      facets: SelectedFacet[];
      text?: string;
      size?: number;
    }
  | {
      type: 'setSearchText';
      value: string | undefined;
    }
  | {
      type: 'paginate';
    };

function resetPaginationParams(state: SearchParams): SearchParams {
  return { ...state, size: undefined };
}

function selectedFacetsReducer(state: SearchParams, action: FacetAction): SearchParams {
  switch (action.type) {
    case 'setValueFacet':
      // eslint-disable-next-line no-case-declarations -- auto-generated TECH-258
      const facets = state.facets
        .filter((selectedFacet) => selectedFacet.key !== action.key)
        .concat({ key: action.key, value: action.value });
      return { ...resetPaginationParams(state), facets };
    case 'resetFacet':
      // eslint-disable-next-line no-case-declarations -- auto-generated TECH-258
      const dependentKeys = Facets.dependentKeys(action.key);
      // eslint-disable-next-line no-case-declarations -- auto-generated TECH-258
      const keysToReset: FacetKey[] = [action.key].concat(dependentKeys ? dependentKeys : []);
      return {
        ...resetPaginationParams(state),
        facets: state.facets.filter((selectedFacet) => !keysToReset.some((key) => selectedFacet.key === key)),
      };
    case 'setSearchText':
      return { ...resetPaginationParams(state), text: action.value };
    case 'setAllFacets':
      return { ...state, facets: action.facets, text: action.text, size: action.size };
    case 'paginate':
      return { ...state, size: state.size ? state.size + PAGINATION_SIZE : PAGINATION_SIZE * 2 };
    default:
      return state;
  }
}

interface SearchContextProviderProps {
  service: AppSearchService;
  storeSearchParams: SearchParams | undefined;
  fetchCallBack: (SearchParams: SearchParams, isPagination?: boolean) => Promise<void>;
  indexType: IndexType;
  routeParams: SearchParams;
  urlCreator: (params: SearchParams) => string;
  facetSubType?: Facets.FacetSubTypes[];
  children?: React.ReactNode;
}

// always start with the url params
export function areSearchParamsEqual(
  urlParams: SearchParams | undefined,
  params2: SearchParams | undefined,
  ignoreSize = false
) {
  if (!urlParams && !params2) return true;
  if (!urlParams || !params2) return false;
  if (urlParams.index !== params2.index) return false;
  // all falsy string values are equal to us

  if (urlParams.text != params2.text) return false;
  // we only care about the size if its in the url
  if (!ignoreSize && urlParams.size && urlParams.size !== params2.size) return false;
  return isEqual(sortBy(urlParams.facets, ['key']), sortBy(params2.facets, ['key']));
}

export const SearchContextProvider: React.FunctionComponent<SearchContextProviderProps> = (props) => {
  const { service, fetchCallBack, urlCreator, routeParams } = props;
  const history = useHistory();
  const indexType = useDeepCompareMemoize(props.indexType);
  const routeSearchParams = useDeepCompareMemoize(routeParams);
  const isUserLoggedIn = useSelector(isUserSignedIn());

  const [filters, dispatch] = useReducer(selectedFacetsReducer, {
    index: indexType,
    text: routeSearchParams.text || '',
    isMulti: routeSearchParams.isMulti || undefined,
    facets: routeSearchParams.facets || [],
    size: routeSearchParams.size,
  });

  const updateSearchQuery = useCallback((action: FacetAction) => {
    dispatch(action);
  }, []);

  const setFacet = (key: FacetKey, value: string[]) => {
    updateSearchQuery({ type: 'setValueFacet', key, value });
  };

  const resetFacet = (key: FacetKey) => {
    updateSearchQuery({ type: 'resetFacet', key });
  };

  const setSearchText = (value: string | undefined) => {
    updateSearchQuery({ type: 'setSearchText', value });
  };

  const paginate = useCallback(() => {
    dispatch({ type: 'paginate' });
  }, []);

  // watch filters and sync to url
  useEffect(() => {
    const syncUrl = () => {
      const filterBasedUrl = urlCreator(filters);
      const currentParamUrl = urlCreator(routeSearchParams);
      if (filterBasedUrl !== currentParamUrl) {
        if (SearchParams.replaceHistoryState(filters, routeSearchParams)) {
          history.replace(filterBasedUrl);
        } else {
          history.push(filterBasedUrl);
        }
        service.storeSearchQueryString(filterBasedUrl);
      }
    };
    syncUrl();
  }, [filters, history, service, routeSearchParams, urlCreator]);

  // watch the route params and fetch if mismatch
  useCallbackIfParamsChanged({
    initial: props.storeSearchParams,
    params: routeSearchParams,
    isEqual: (a, b) => areSearchParamsEqual(a, b, false),
    callback(params, previous) {
      if (!params) return;
      const isPagination = areSearchParamsEqual(params, previous, true);
      fetchCallBack(params, isPagination);
    },
    changeOnUserChange: true,
    debug: false,
    isUserLoggedIn,
  });

  return (
    <SearchContext.Provider
      value={{
        currentParams: () => cloneDeep(filters),
        selectedFacets: filters.facets,
        selectedText: filters.text,
        resetFacet,
        setFacet,
        setSearchText,
        paginate,
      }}
    >
      {props.children}
    </SearchContext.Provider>
  );
};
